import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/app/rootReducer";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

import EmailNotificationsFilters from "@/features/adminEmailNotifications/components/EmailNotificationsFilters/EmailNotificationsFilters";

import {
  fetchEmailNotificationsAction,
  approveEmailNotificationAction,
} from "@/features/adminEmailNotifications/slices/emailNotificationSlice";
import { EmailNotification } from "@/api/getEmailNotifications/types";
import { createEmailNotificationsColumns } from "@/features/adminEmailNotifications/components/EmailNotificationsColumns/EmailNotificationsColumns";
import EmailNotificationPreview from "@/features/adminEmailNotifications/components/EmailNotificationPreview/EmailNotificationPreview";

// Import our new ApproveNotificationModal
import ApproveNotificationModal from "@/features/adminEmailNotifications/components/ApproveNotificationModal/ApproveNotificationModal";

function EmailNotificationsList() {
  const dispatch = useDispatch();

  // Redux store data
  const notifications = useSelector(
    (state: RootState) => state.emailNotifications.notifications,
  );
  const totalCount = useSelector(
    (state: RootState) => state.emailNotifications.totalCount,
  );
  const loading = useSelector(
    (state: RootState) => state.emailNotifications.getLoading,
  );
  const error = useSelector(
    (state: RootState) => state.emailNotifications.getError,
  );

  // Local state for sorting, pagination, filters
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState({
    searchTerm: "",
    approved: "all",
    sent: "all",
  });

  // Approve flow states
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [notificationToApprove, setNotificationToApprove] =
    useState<EmailNotification | null>(null);

  // Preview flow states
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [notificationToPreview, setNotificationToPreview] =
    useState<EmailNotification | null>(null);

  // Re-fetch notifications from the server
  const refetchNotifications = useCallback(() => {
    const sortBy = sorting.length ? sorting[0].id : undefined;
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "DESC"
        : "ASC"
      : undefined;

    let approvedVal: boolean | undefined;
    if (filters.approved === "approved") approvedVal = true;
    if (filters.approved === "unapproved") approvedVal = false;

    let sentVal: boolean | undefined;
    if (filters.sent === "sent") sentVal = true;
    if (filters.sent === "unsent") sentVal = false;

    dispatch(
      fetchEmailNotificationsAction({
        searchTerm: filters.searchTerm || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder: sortOrder as "ASC" | "DESC" | undefined,
        approved: approvedVal,
        sent: sentVal,
      }),
    );
  }, [dispatch, sorting, filters, pagination]);

  // Fetch on mount, or whenever [sorting, filters, pagination] changes
  useEffect(() => {
    refetchNotifications();
  }, [refetchNotifications]);

  // Approve flow
  const handleAskApprove = useCallback((notification: EmailNotification) => {
    setNotificationToApprove(notification);
    setConfirmModalOpen(true);
  }, []);

  const handleApproveConfirm = useCallback(async () => {
    if (notificationToApprove) {
      await dispatch(
        approveEmailNotificationAction({
          notificationId: notificationToApprove.id,
        }),
      );
    }
    setConfirmModalOpen(false);
    setNotificationToApprove(null);
  }, [dispatch, notificationToApprove]);

  const handleApproveCancel = useCallback(() => {
    setConfirmModalOpen(false);
    setNotificationToApprove(null);
  }, []);

  // Preview flow
  const handleAskPreview = useCallback((notification: EmailNotification) => {
    setNotificationToPreview(notification);
    setPreviewModalOpen(true);
  }, []);

  const handlePreviewClose = useCallback(() => {
    setPreviewModalOpen(false);
    setNotificationToPreview(null);
  }, []);

  // Build columns with our callbacks
  const columns = useMemo(
    () =>
      createEmailNotificationsColumns({
        onAskApprove: handleAskApprove,
        onAskPreview: handleAskPreview,
      }),
    [handleAskApprove, handleAskPreview],
  );

  // Pagination/sorting handlers
  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  // Filter changes
  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    // Reset page to 0 whenever filters change
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <MainPageWrapper error={error} title="Email Notifications">
      <EmailNotificationsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={notifications}
        error={error}
        loading={loading}
        noDataMessage="No email notifications found"
        onPageChange={(pageIndex, pageSize) =>
          handlePaginationChange({
            pageIndex,
            pageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => String(item.id)}
        sorting={sorting}
        totalCount={totalCount}
      />

      {/* Full-screen Preview Modal */}
      <EmailNotificationPreview
        isOpen={previewModalOpen}
        notification={notificationToPreview}
        onClose={handlePreviewClose}
      />

      {/* Approve Notification Modal */}
      <ApproveNotificationModal
        isOpen={confirmModalOpen}
        notification={notificationToApprove}
        onClose={handleApproveCancel}
        onConfirm={handleApproveConfirm}
      />
    </MainPageWrapper>
  );
}

export default EmailNotificationsList;
